<template>
  <Modal ref="modal" position="center">
    <template v-if="user?.role !== Role.READ_ONLY" #activator="{ show }">
      <ActionButton color="secondary" @click="show">
        <PlusIcon class="-ml-0.5 size-5 stroke-2" aria-hidden="true" />
        Add Project
      </ActionButton>
    </template>

    <template #header="{ hide }">
      <p
        class="text-primary text-lg font-semibold capitalize text-center flex gap-2 items-center"
      >
        Add Project
      </p>
      <button
        class="bg-light-neutral-500 rounded-full h-[30px] w-[30px] flex items-center justify-center"
        @click="hide"
      >
        <XIcon class="size-4 stroke-2 text-mid-neutral-500" />
      </button>
    </template>

    <template #content="{ hide }">
      <div class="w-full space-y-4">
        <TextInput
          ref="projectNameInput"
          v-model="projectName"
          label="Project name"
          placeholder="Enter project name"
          type="text"
          :rules="[(v) => !!v || 'Project name is required']"
          @enter="createProject(hide)"
        />
      </div>
    </template>

    <template #footer="{ hide }">
      <div class="flex justify-end items-center gap-2">
        <ActionButton color="neutral" @click="hide"> Cancel </ActionButton>
        <ActionButton color="primary" @click="createProject(hide)">
          Confirm
        </ActionButton>
      </div>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import useProjects from '@app/views/Projects/use-projects'
import { onMounted, ref } from 'vue'
import TextInput from '@app/components/Global/Inputs/TextInput.vue'
import useSnackbar from '@app/composables/use-snackbar'
import useLoading from '@app/composables/use-loading'
import { SnackbarState } from '@app/types'
import { errorMessage } from '@app/utils/error-message'
import { HttpException } from '@core/exceptions/http.exception'
import { Role } from '@core/domain/types/role.type'
import Modal from '@app/components/Global/Modal/Modal.vue'
import XIcon from '@app/components/Icons/XIcon.vue'
import { injectStrict } from '@app/utils/injectStrict'
import { AuthKey, TeamsServiceKey } from '@app/injectionKeys'
import { PlusIcon } from '@heroicons/vue/24/outline'
import { Team } from '@core/domain/models/team.model'
import ActionButton from '@app/components/Global/ActionButton.vue'

const teamsService = injectStrict(TeamsServiceKey)
const { user } = injectStrict(AuthKey)
const projects = useProjects()
const snackbar = useSnackbar()
const loading = useLoading()
const projectName = ref('')
const selectedTeam = ref<Team>()
const teams = ref<Team[]>([])
const projectNameInput = ref<InstanceType<typeof TextInput>>()
const emit = defineEmits<{
  projectCreated: [number]
}>()
async function createProject(cb: () => void) {
  if (!projectNameInput.value?.validate()) return
  if (!selectedTeam.value) return

  try {
    loading.start()
    const createdProject = await projects.create({
      name: projectName.value,
      teamId: selectedTeam.value.id,
    })
    emit('projectCreated', createdProject.id)
    snackbar.success('Success')
    cb()
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    if (error.response.data.statusCode >= 500) {
      throw e
    }
  } finally {
    projectName.value = ''
    loading.stop()
  }
}

onMounted(async () => {
  teams.value = await teamsService.findTeams()
  const defaultTeam = teams.value.find((team) => team.name === 'Default')
  if (defaultTeam) {
    selectedTeam.value = defaultTeam
  }
})
</script>
